import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  SaveSelfAssessmentResultsDocument,
  SelfAssessmentResultDocument,
  SelfAssessmentStatementsDocument,
} from "@/graphql/generated/operations";
import { BackButton } from "@/components/common/BackButton";
import {
  AssessmentFlow,
  type AssessmentItem,
} from "@/components/grow/AssessmentFlow";
import { ELEMENT_CODES, elementDisplayName, elementImageSrc, elementNameId } from "@/lib/elements";
import {
  type AnswerMap,
  buildSaveResultsInput,
  LIKERT_LEVELS,
} from "@/lib/self-assessment";
import { useAgentStore } from "@/stores/agent-store";
import { logApplicationFailure } from "@/lib/user-error";
import { CODES } from "@/lib/diagnostics/contract";

// Per-element self-assessment. URL :elementId is one of the 30 CreativityElement
// values (e.g. PERSEVERANCE). The answering UI is the shared AssessmentFlow
// template; this wrapper owns the statements query, the Likert mapping, the
// incremental autosave and the hand-off to the result screen.

type SaveStatus = "idle" | "saving" | "saved" | "error";

function isKnownElement(value: string): boolean {
  return value in ELEMENT_CODES;
}

export function ElementAssessmentPage() {
  const intl = useIntl();
  const ask = useAgentStore((s) => s.ask);
  const navigate = useNavigate();
  const params = useParams<{ elementId: string }>();
  const elementParam = params.elementId ?? "";
  const valid = isKnownElement(elementParam);

  const [answers, setAnswers] = useState<AnswerMap>({});
  // Snapshot of what's persisted; the diff drives the submit toast.
  const [savedAnswers, setSavedAnswers] = useState<AnswerMap>({});
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");

  const { data, loading } = useQuery(SelfAssessmentStatementsDocument, {
    // The codegen narrows the variable type to CreativityElement; cast through
    // string because lib/elements.ts widens elements for cross-file typing.
    variables: { element: elementParam as never },
    skip: !valid,
    // Always revalidate: a stale empty result (e.g. cached before the questions
    // were imported) must not stick. genesis does not cache this resolver.
    fetchPolicy: "cache-and-network",
  });
  const [saveResults] = useMutation(SaveSelfAssessmentResultsDocument);
  // Pre-save snapshot of score + rank, so the result screen can show the deltas
  // (achievement.level / achievement.rank). Read from cache; refreshed on the
  // result page network-side after the save lands.
  const { data: beforeData } = useQuery(SelfAssessmentResultDocument, {
    variables: { element: elementParam as never },
    skip: !valid,
  });

  // genesis returns the statements already ordered (by sam_question_id); the
  // `order` field is unusable (declared Int! but always null), so keep server order.
  const statements = useMemo(
    () => data?.selfAssessmentStatements ?? [],
    [data?.selfAssessmentStatements],
  );
  const questionIds = useMemo(() => statements.map((s) => s.id), [statements]);

  // The 5-point Likert, each option labelled. The chosen level (1..5) is
  // stored; mapped to a 0-100 scorePercent only on save (lib/self-assessment).
  // Reverse questions (invertResult) are handled server-side, so we send the
  // raw selection.
  const items: AssessmentItem[] = useMemo(
    () =>
      statements.map((s) => ({
        id: s.id,
        text: `„${s.content.localized ?? ""}"`,
        options: LIKERT_LEVELS.map((l) => ({
          key: String(l.level),
          title: l.level,
          label: intl.formatMessage({ id: l.labelId }),
        })),
      })),
    [statements, intl],
  );
  const selected = useMemo(() => {
    const out: Record<string, string | undefined> = {};
    for (const id of questionIds) {
      out[id] = answers[id] !== undefined ? String(answers[id]) : undefined;
    }
    return out;
  }, [answers, questionIds]);

  const total = statements.length;
  const answeredCount = questionIds.reduce(
    (n, id) => (answers[id] !== undefined ? n + 1 : n),
    0,
  );
  const complete = total > 0 && answeredCount === total;
  const dirty = questionIds.some((id) => answers[id] !== savedAnswers[id]);

  // Reveal: image is fully transparent + heavily blurred at 0 answered, and
  // turns fully opaque + sharp once every statement is rated. Blur keeps it
  // unrecognisable until late so the user stays curious.
  const revealSrc = elementImageSrc(elementParam);
  const revealProgress = total > 0 ? answeredCount / total : 0;
  const revealOpacity = revealProgress;
  const revealBlur = 20 * (1 - revealProgress);

  const submit = useCallback(async () => {
    if (!complete || saveStatus === "saving") return;
    setSaveStatus("saving");
    try {
      await saveResults({
        variables: { input: buildSaveResultsInput(answers, questionIds) },
      });
      setSavedAnswers(answers);
      // Hand the pre-save score + rank to the result screen so it can show the
      // gained score / new rank against the fresh (network-refetched) values.
      navigate(`/grow/${elementParam}/result`, {
        state: {
          prevScore: beforeData?.growRanking.score ?? null,
          prevRank: beforeData?.growRanking.userRank ?? null,
        },
      });
    } catch {
      logApplicationFailure("grow.save-self-assessment");
      setSaveStatus("error");
    }
  }, [complete, saveStatus, answers, questionIds, saveResults, navigate, elementParam, beforeData]);

  // Incremental autosave: genesis upserts per (question, person) and never
  // deletes, so persisting partial progress is safe. Debounced; only the
  // answers that changed since the last persist are sent, so re-answering one
  // question writes one row, not the whole set. This is what makes the Grow
  // dashboard's "x/y answered" reflect reality between visits.
  useEffect(() => {
    const pending = questionIds.filter(
      (id) => answers[id] !== undefined && answers[id] !== savedAnswers[id],
    );
    if (pending.length === 0) return;
    const timer = setTimeout(async () => {
      const delta: AnswerMap = {};
      for (const id of pending) delta[id] = answers[id];
      setSaveStatus("saving");
      try {
        await saveResults({ variables: { input: buildSaveResultsInput(delta, pending) } });
        setSavedAnswers((prev) => ({ ...prev, ...delta }));
        setSaveStatus("saved");
      } catch {
        logApplicationFailure("grow.autosave-self-assessment", {
          code: CODES.application.AUTOSAVE_FAILURE,
        });
        setSaveStatus("error");
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [answers, savedAnswers, questionIds, saveResults]);

  if (!valid) {
    return (
      <section className="mx-auto max-w-screen-md px-4 py-12" data-graphql="SelfAssessmentStatements">
        <h1 className="text-2xl font-semibold">
          <FormattedMessage id="grow.unknownElement" defaultMessage="Unknown element" />
        </h1>
        <p className="mt-2 text-sm text-ink/55">{elementParam}</p>
      </section>
    );
  }

  return (
    <section
      className="mx-auto max-w-screen-md px-4 py-12"
      data-graphql="SelfAssessmentStatements, SelfAssessmentResult, SaveSelfAssessmentResults"
    >
      <div className="mb-5">
        <BackButton />
      </div>
      <header className="mb-8 flex items-start justify-between gap-6">
        <div>
          <p className="text-xs tracking-wide text-ink/55">
            <FormattedMessage id="grow.title" defaultMessage="Grow" />
          </p>
          <h1 className="mt-1 text-3xl font-semibold">{intl.formatMessage({ id: elementNameId(elementParam), defaultMessage: elementDisplayName(elementParam) })}</h1>
          <p className="mt-2 text-sm text-ink/55">
            <FormattedMessage
              id="grow.assessmentSubtitle"
              defaultMessage="Rate yourself on each statement below."
            />
          </p>
        </div>
        {revealSrc ? (
          // The power artwork starts fully hidden and uncovers itself one
          // answered question at a time, so the user gets curious what it is.
          <div className="relative hidden aspect-[16/10] w-64 shrink-0 overflow-hidden rounded-xl bg-ink/[0.06] ring-1 ring-ink/10 @sm:block">
            <img
              src={revealSrc}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full scale-110 object-cover transition-all duration-700 ease-out"
              style={{ opacity: revealOpacity, filter: `blur(${revealBlur}px)` }}
            />
          </div>
        ) : null}
      </header>

      {loading && total === 0 ? (
        <p className="py-8 text-sm text-ink/55">
          <FormattedMessage id="common.loading" defaultMessage="Loading…" />
        </p>
      ) : total === 0 ? (
        <p className="py-8 text-sm text-ink/55">
          <FormattedMessage id="grow.noQuestions" defaultMessage="No questions for this element yet." />
        </p>
      ) : (
        <AssessmentFlow
          items={items}
          selected={selected}
          onSelect={(id, key) => setAnswers((prev) => ({ ...prev, [id]: Number(key) }))}
          onAskAboutItem={(item) => {
            // Always open the drawer (right side) + send — `ask` sets open:true and queues the prompt.
            // Prompt with the RAW statement (item.text has the „ " quote wrapper baked in), randomizing
            // among 10 equivalent phrasings (grow.elementAssessment.askIntro.0..9) for variety.
            const raw = statements.find((s) => s.id === item.id)?.content.localized ?? item.text;
            const v = Math.floor(Math.random() * 10);
            ask(intl.formatMessage({ id: `grow.elementAssessment.askIntro.${v}` }, { statement: raw }));
          }}
          itemLabel={(index) => (
            <FormattedMessage
              id="grow.elementAssessment.statementLabel"
              defaultMessage="Statement {index}"
              values={{ index }}
            />
          )}
          statusSuffix={
            saveStatus === "error" ? (
              <span className="text-accent">
                {" "}
                · <FormattedMessage id="grow.elementAssessment.notSaved" defaultMessage="not saved" />
              </span>
            ) : dirty || saveStatus === "saving" ? (
              <span className="text-ink/40">
                {" "}
                · <FormattedMessage id="grow.elementAssessment.savingStatus" defaultMessage="saving…" />
              </span>
            ) : answeredCount > 0 ? (
              <span className="text-ink/40">
                {" "}
                · <FormattedMessage id="grow.elementAssessment.savedStatus" defaultMessage="saved" />
              </span>
            ) : null
          }
          saving={saveStatus === "saving"}
          saveError={saveStatus === "error"}
          submitLabel={
            <FormattedMessage id="grow.elementAssessment.toEvaluation" defaultMessage="To evaluation" />
          }
          submitTitle={intl.formatMessage({
            id: "grow.elementAssessment.toEvaluation",
            defaultMessage: "To evaluation",
          })}
          onSubmit={() => void submit()}
        />
      )}
    </section>
  );
}
